/**
 * Cognito Hosted UI con Authorization Code + PKCE.
 *
 * Flujo completo:
 *  1. `startLogin()` genera code_verifier + state, los guarda en
 *     sessionStorage y redirige a la Hosted UI con el code_challenge (S256).
 *  2. Al volver, `consumeAuthCode()` valida el state (anti-CSRF), canjea el
 *     `code` en POST {domain}/oauth2/token con el verifier y devuelve los
 *     tokens (id + access + refresh).
 *  3. `refreshWithToken()` renueva con grant_type=refresh_token; lo llaman
 *     el temporizador proactivo del AuthContext y el reintento de 401 del
 *     cliente HTTP.
 *
 * El pool tiene el implicit grant DESACTIVADO (ver infra/lib/auth.ts):
 * este es el unico flujo que funciona, y el unico que da refresh token.
 */

const VERIFIER_KEY = 'alexiarest.pkce.verifier';
const STATE_KEY = 'alexiarest.pkce.state';
const RETURN_KEY = 'alexiarest.auth.returnTo';

export interface CognitoConfig {
  domain: string;
  clientId: string;
  redirectUri: string;
  scope: string;
  /** Proveedores externos activos en el pool (hoy solo Google). */
  identityProviders: string[];
}

export interface TokenSet {
  idToken: string;
  refreshToken?: string;
  /** Segundos de vida que anuncia Cognito (los del access/id token). */
  expiresIn?: number;
}

export function getCognitoConfig(): CognitoConfig | null {
  const domain = (import.meta.env.VITE_COGNITO_DOMAIN ?? '').trim().replace(/\/+$/, '');
  const clientId = (import.meta.env.VITE_COGNITO_CLIENT_ID ?? '').trim();
  if (!domain || !clientId) return null;

  return {
    domain,
    clientId,
    redirectUri:
      (import.meta.env.VITE_COGNITO_REDIRECT_URI ?? '').trim() || `${window.location.origin}/`,
    scope: 'openid email profile',
    // El pool solo ofrece Google si Pol ha configurado la credencial: la web
    // se entera por esta variable, no adivinando.
    identityProviders:
      (import.meta.env.VITE_COGNITO_IDPS ?? '')
        .split(',')
        .map((p) => p.trim())
        .filter((p) => p.length > 0),
  };
}

/** ¿Se puede ofrecer el boton de Google en este entorno? */
export function hasGoogleLogin(config: CognitoConfig | null): boolean {
  return config?.identityProviders.some((p) => p.toLowerCase() === 'google') ?? false;
}

/* ------------------------------------------------------------------ */
/* PKCE                                                                */
/* ------------------------------------------------------------------ */

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomToken(bytes = 32): string {
  const buffer = new Uint8Array(bytes);
  window.crypto.getRandomValues(buffer);
  return base64Url(buffer);
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return base64Url(new Uint8Array(digest));
}

/* ------------------------------------------------------------------ */
/* Login / logout                                                      */
/* ------------------------------------------------------------------ */

/**
 * Genera verifier+state, los guarda y redirige a la Hosted UI.
 *
 * Con `identityProvider` se salta la pantalla de Cognito y va directo al
 * proveedor: quien pulsa "Continuar con Google" espera ver Google, no un
 * formulario intermedio con un boton mas.
 */
export async function startLogin(
  config: CognitoConfig,
  identityProvider?: string,
  options: { signup?: boolean; loginHint?: string } = {},
): Promise<void> {
  const verifier = randomToken(48);
  const state = randomToken(16);
  try {
    window.sessionStorage.setItem(VERIFIER_KEY, verifier);
    window.sessionStorage.setItem(STATE_KEY, state);
    const returnTo = `${window.location.pathname || '/'}${window.location.search || ''}`;
    if (returnTo !== '/') window.sessionStorage.setItem(RETURN_KEY, returnTo);
  } catch {
    // Sin sessionStorage (modo privado extremo) el flujo no puede validar
    // el retorno; mejor avisar que fallar en silencio al volver.
  }

  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: config.scope,
    redirect_uri: config.redirectUri,
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
  });
  if (identityProvider) params.set('identity_provider', identityProvider);
  if (options.loginHint) params.set('login_hint', options.loginHint);
  // Quien viene del chat de la landing aún no tiene cuenta: directo al
  // formulario de registro del managed login, que acepta los mismos parámetros.
  const ruta = options.signup && !identityProvider ? '/signup' : '/oauth2/authorize';
  window.location.assign(`${config.domain}${ruta}?${params.toString()}`);
}

export function buildLogoutUrl(config: CognitoConfig): string {
  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.redirectUri,
  });
  return `${config.domain}/logout?${params.toString()}`;
}

/* ------------------------------------------------------------------ */
/* Canje del code y refresco                                           */
/* ------------------------------------------------------------------ */

async function tokenRequest(
  config: CognitoConfig,
  body: Record<string, string>,
): Promise<TokenSet | null> {
  const response = await fetch(`${config.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: config.clientId, ...body }).toString(),
  });
  if (!response.ok) return null;
  const data = (await response.json()) as {
    id_token?: string;
    refresh_token?: string;
    expires_in?: number;
  };
  if (!data.id_token) return null;
  return {
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    expiresIn: data.expires_in,
  };
}

/**
 * Si la URL trae `?code=` de la Hosted UI, valida el state, canjea el code
 * y limpia la URL. Devuelve null si no venimos de un login (caso normal).
 * Lanza si el state no cuadra: eso es un intento de CSRF o un retorno
 * corrupto, y NO se acepta el code.
 */
export async function consumeAuthCode(config: CognitoConfig): Promise<TokenSet | null> {
  // La vuelta del OAuth de un TPV (Loyverse, Square) tambien llega con
  // `?code=&state=`, pero a /integraciones/<tpv>/callback: ese code es del
  // TPV y lo canjea la API, no Cognito. Sin esto, la primera conexion real
  // con Loyverse acabo en «el retorno del inicio de sesion no es valido» y
  // con la sesion cerrada (4 sep 2026).
  if (window.location.pathname.startsWith('/integraciones/')) return null;
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) return null;
  // Compatibilidad con los primeros enlaces Founder, que usaban `?code=`.
  // No son callbacks OAuth y por tanto no deben consumir ni borrar la URL.
  if (code.startsWith('FOUNDER-') && !params.has('state')) return null;

  const returnedState = params.get('state') ?? '';
  let expectedState = '';
  let verifier = '';
  try {
    expectedState = window.sessionStorage.getItem(STATE_KEY) ?? '';
    verifier = window.sessionStorage.getItem(VERIFIER_KEY) ?? '';
    window.sessionStorage.removeItem(STATE_KEY);
    window.sessionStorage.removeItem(VERIFIER_KEY);
  } catch {
    /* sin sessionStorage no hay verifier: el canje fallara y se reintenta */
  }

  // Limpia code y state de la URL ANTES de nada: que no queden en historial.
  params.delete('code');
  params.delete('state');
  const query = params.toString();
  window.history.replaceState(
    null,
    '',
    window.location.pathname + (query ? `?${query}` : ''),
  );

  if (!expectedState || returnedState !== expectedState) {
    throw new Error('El retorno del inicio de sesión no es válido. Vuelve a entrar.');
  }

  return tokenRequest(config, {
    grant_type: 'authorization_code',
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier,
  });
}

/** Recupera una ruta interna guardada antes de saltar a Cognito. */
export function takeLoginReturn(): string | null {
  try {
    const value = window.sessionStorage.getItem(RETURN_KEY);
    window.sessionStorage.removeItem(RETURN_KEY);
    // Solo rutas internas absolutas; nunca se acepta una URL ni `//host`.
    return value?.startsWith('/') && !value.startsWith('//') ? value : null;
  } catch {
    return null;
  }
}

/** Renueva los tokens con el refresh token. null = sesion caducada de verdad. */
export async function refreshWithToken(
  config: CognitoConfig,
  refreshToken: string,
): Promise<TokenSet | null> {
  const set = await tokenRequest(config, {
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
  });
  // Cognito no rota el refresh token en este grant: se conserva el actual.
  if (set && !set.refreshToken) set.refreshToken = refreshToken;
  return set;
}
